import { RuntimeProbeTraceV1Schema } from '@mintyads/shared-types';
import type {
  RuntimeProbeActionRecord,
  RuntimeProbeSnapshot,
  RuntimeProbeTrace,
} from './runtimeProbeTypes';

export interface RuntimeProbeTraceBufferOptions {
  readonly projectId: string;
  readonly scenario: string;
  readonly startedAtMs: number;
  readonly maxSnapshots?: number | undefined;
}

export interface RuntimeProbeTraceBuffer {
  addSnapshot(snapshot: RuntimeProbeSnapshot): void;
  addAction(action: RuntimeProbeActionRecord): void;
  finish(finishedAtMs: number): RuntimeProbeTrace;
}

function uniqueWarnings(snapshots: readonly RuntimeProbeSnapshot[]): string[] {
  const seen = new Set<string>();
  const warnings: string[] = [];
  for (const snapshot of snapshots) {
    for (const warning of snapshot.warnings) {
      if (seen.has(warning)) continue;
      seen.add(warning);
      warnings.push(warning);
    }
  }
  return warnings;
}

export function createRuntimeProbeTraceBuffer(options: RuntimeProbeTraceBufferOptions): RuntimeProbeTraceBuffer {
  const maxSnapshots = Math.max(1, options.maxSnapshots ?? 240);
  const snapshots: RuntimeProbeSnapshot[] = [];
  const actions: RuntimeProbeActionRecord[] = [];

  return {
    addSnapshot(snapshot) {
      snapshots.push(snapshot);
      while (snapshots.length > maxSnapshots) snapshots.shift();
    },
    addAction(action) {
      actions.push(action);
    },
    finish(finishedAtMs) {
      const trace = {
        schemaVersion: 'runtime_probe_trace.v1',
        projectId: options.projectId,
        scenario: options.scenario,
        startedAtMs: options.startedAtMs,
        finishedAtMs,
        snapshots: [...snapshots],
        actions: [...actions],
        summary: {
          layerCount: Math.max(0, ...snapshots.map((snapshot) => snapshot.layers.length)),
          inputEvents: snapshots.reduce((total, snapshot) => total + snapshot.recentInput.length, 0),
          tweenCount: snapshots.reduce((total, snapshot) => total + snapshot.activeTweens.length, 0),
          warnings: uniqueWarnings(snapshots),
        },
      };
      return RuntimeProbeTraceV1Schema.parse(trace);
    },
  };
}
